<script lang="ts">
import { computed } from 'vue';

import { modals } from '@modals';

declare module 'vue/types/vue' {
  export interface Vue {
    CardSiteLicenses: ComponentWithProps<Props>;
  }
}

export default { name: 'CardSiteLicenses' };
</script>

<script setup lang="ts">
/**
 * `CardStat` component properties.
 */
export interface Props {
  maxStudentLicenses: number | undefined;
  usedStudentLicenses: number | undefined;
  maxInstructorLicenses?: number | undefined;
  usedInstructorLicenses?: number | undefined;
  maxAdminLicenses?: number | undefined;
  usedAdminLicenses?: number | undefined;
  loading: boolean;
}

const props = defineProps<Props>();

const title = 'SITE LICENSE USAGE';

const successVariantValue = 90;
const warningVariantValue = 100;

function getLicenseVariant(max: number, used: number) {
  if (!max || !used) {
    return 'success';
  }

  const percentage = (used / max) * 100;

  if (percentage < successVariantValue) {
    return 'success';
  } else if (percentage <= warningVariantValue) {
    return 'warning';
  } else {
    return 'danger';
  }
}

const studentLicenseVariant = computed(() => {
  if (!props.maxStudentLicenses || !props.usedStudentLicenses) {
    return 'success';
  }

  return getLicenseVariant(props.maxStudentLicenses, props.usedStudentLicenses);
});

const addMoreLicenses = () => {
  void modals.util.generalInfo({
    title: 'Add More Licenses',
    text: 'To add more licenses, contact your account manager or email us at sales@littlearms.com',
  });
};
</script>

<template>
  <CardLoadable v-bind="{ loading: !!loading }" no-body>
    <b-card-body>
      <b-card-title>
        <div class="h6">
          <span class="card-title">{{ title }}</span>
        </div>

        <div>
          <slot name="header" />
        </div>
      </b-card-title>

      <b-container>
        <!-- Student Licenses -->
        <b-row class="license-usage-item">
          <b-col cols="3">
            <div class="license-usage-item-label">
              <span>Students</span>
            </div>
          </b-col>

          <b-col cols="7" style="padding-top: 4px">
            <b-progress
              :value="usedStudentLicenses"
              :variant="studentLicenseVariant"
              :max="maxStudentLicenses"
            />
          </b-col>
          <b-col cols="2" style="text-align: center">
            <div class="license-usage-item-total">
              {{ usedStudentLicenses }}/{{ maxStudentLicenses }}
            </div>
          </b-col>
        </b-row>

        <template
          v-if="maxInstructorLicenses && usedInstructorLicenses !== undefined"
        >
          <hr />

          <!-- Instructor Licenses -->
          <b-row class="license-usage-item">
            <b-col cols="3">
              <div class="license-usage-item-label">
                <span>Instructors</span>
              </div>
            </b-col>

            <b-col cols="7" style="padding-top: 4px">
              <b-progress
                :value="usedInstructorLicenses"
                variant="info"
                :max="maxInstructorLicenses"
              />
            </b-col>
            <b-col cols="2" style="text-align: center">
              <div class="license-usage-item-total">
                {{ usedInstructorLicenses }}/{{ maxInstructorLicenses }}
              </div>
            </b-col>
          </b-row>
        </template>

        <template v-if="maxAdminLicenses && usedAdminLicenses !== undefined">
          <!-- Admin Licenses -->
          <b-row class="license-usage-item">
            <b-col cols="3">
              <div class="license-usage-item-label">
                <span>Admins</span>
              </div>
            </b-col>

            <b-col cols="7" style="padding-top: 4px">
              <b-progress
                :value="usedAdminLicenses"
                variant="info"
                :max="maxAdminLicenses"
              />
            </b-col>
            <b-col cols="2" style="text-align: center">
              <div class="license-usage-item-total">
                {{ usedAdminLicenses }}/{{ maxAdminLicenses }}
              </div>
            </b-col>
          </b-row>
        </template>
      </b-container>
    </b-card-body>

    <b-card-footer class="footer-container">
      <b-button
        variant="outline-primary"
        size="sm"
        pill
        @click="addMoreLicenses"
      >
        Need More Licenses?
      </b-button>
    </b-card-footer>
  </CardLoadable>
</template>

<style lang="scss" scoped>
.card-title {
  display: flex;
  justify-content: space-between;
}

.card-title {
  text-transform: uppercase;
  letter-spacing: 0.2rem;
  font-size: 0.9rem;
}

.license-usage-container {
  .license-usage-item {
    display: flex;
    flex-direction: row;
    align-items: center;

    .b-col {
      padding-left: 3px;
      padding-right: 3px;
    }
  }
}

.footer-container {
  display: flex;
  justify-content: flex-end;
}
</style>
